import { useEffect, useState } from 'react';
import html2canvas from 'html2canvas';

/**
 * Hook that captures a screenshot of the page when the modal opens.
 * Only ignores elements marked with `data-feedback-ignore` (the feedback
 * modal and FAB themselves) — any other dialogs the user has open will be
 * included in the capture.
 */
export function useScreenshotCapture(open: boolean) {
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCapturing(true);

    const timer = setTimeout(async () => {
      try {
        const canvas = await html2canvas(document.body, {
          ignoreElements: (el) => {
            // Only skip our own feedback UI — NOT other dialogs the user has open
            if (el.classList?.contains('feedback-modal-root')) return true;
            if (el.hasAttribute?.('data-feedback-ignore')) return true;
            return false;
          },
          useCORS: true,
          logging: false,
          scale: Math.min(window.devicePixelRatio, 2),
        });
        setScreenshotUrl(canvas.toDataURL('image/png'));
      } catch (err) {
        console.warn('Screenshot capture failed:', err);
      } finally {
        setCapturing(false);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [open]);

  const reset = () => setScreenshotUrl(null);

  return { screenshotUrl, capturing, resetScreenshot: reset };
}
